import { useState } from 'react';

import type { ClipEntry } from '~/entities/exercise-clip';
import { useSessionUser } from '~/entities/session';
import { preferenceStore } from '~/shared/lib/preferenceStore';

import { dockPanelOpen, opensOnItsOwn, readDockPinned, writeDockPinned } from '../model/formaDockPanel';

export interface FormaDockPanel {
  readonly open: boolean;
  /** The pin or the trainer's own toggle set the width, so no read has to land first. */
  readonly chosenByTrainer: boolean;
  readonly pinned: boolean;
  /** The older recording a double click put in the panel, or `null` for the newest. */
  readonly chosenClipId: number | null;
  readonly togglePin: () => void;
  readonly expand: () => void;
  readonly collapse: () => void;
  readonly showInPanel: (clipId: number) => void;
  readonly showNewest: () => void;
}

interface Chosen {
  readonly exerciseId: number;
  readonly clipId: number;
}

/** The pin, the visit's toggle, the answer latched per movement, and the double-clicked recording. */
export function useFormaDockPanel(
  exerciseId: number | null,
  clips: readonly ClipEntry[] | undefined,
): FormaDockPanel {
  const trainerId = useSessionUser()?.id ?? null;
  const [pinned, setPinned] = useState(() => readDockPinned(preferenceStore(), trainerId));
  const [toggle, setToggle] = useState<boolean | null>(null);
  const [latched, setLatched] = useState<ReadonlyMap<number, boolean>>(new Map());
  const [chosen, setChosen] = useState<Chosen | null>(null);

  // Latched in render, so the render the recordings land in already has its width.
  if (exerciseId !== null && clips !== undefined && !latched.has(exerciseId)) {
    setLatched(new Map(latched).set(exerciseId, opensOnItsOwn(clips)));
  }

  const pin = (next: boolean) => {
    setPinned(next);
    writeDockPinned(preferenceStore(), trainerId, next);
  };

  return {
    open:
      dockPanelOpen({
        pinned,
        toggle,
        latched: exerciseId === null ? null : (latched.get(exerciseId) ?? null),
      }) ?? false,
    chosenByTrainer: pinned || toggle !== null,
    pinned,
    chosenClipId: chosen !== null && chosen.exerciseId === exerciseId ? chosen.clipId : null,
    togglePin: () => {
      pin(!pinned);
      setToggle(true);
    },
    expand: () => {
      setToggle(true);
    },
    collapse: () => {
      if (pinned) pin(false);
      setToggle(false);
      setChosen(null);
    },
    showInPanel: (clipId) => {
      if (exerciseId === null) return;
      setChosen({ exerciseId, clipId });
      setToggle(true);
    },
    showNewest: () => {
      setChosen(null);
    },
  };
}
